// Rapor Tutarlılık Denetimi store'u — ince Zustand; Review.tsx `sections` state'ine dokunmaz.
// start: tahmin → fingerprint ile POST; 409 input_changed'de tahmin yenilenir (UI diyaloğu yeniden açar).
// setFindingStatus: optimistik + hata → geri alma. Polling yalnız queued/running iken (5 sn);
// `load` aktif koşum görürse yoklamayı kendisi kurar (sayfa yenileme / ikinci izleyici SSE kaçırsa da biter).
import { create } from "zustand";
import type {
  FindingStatus,
  ReportCheckDetail,
  ReportCheckEstimate,
  ReportCheckStatus,
  ReportCheckSummary,
} from "@mahir/shared";
import { ApiError } from "../api/client";
import {
  getCurrentReportCheck,
  getReportCheckEstimate,
  listReportChecks,
  patchFinding,
  startReportCheck,
} from "../api/report-check";
import { reportCheckErrorText } from "../lib/report-check-copy";

export type ReportCheckBusy = "idle" | "estimating" | "starting" | "polling";

export interface ReportCheckEvent {
  type: "report_check.status";
  checkId: string;
  status: ReportCheckStatus;
  findingCount?: number;
}

export interface ReportCheckState {
  projectId: string | null;
  current: ReportCheckDetail | null;
  history: ReportCheckSummary[];
  estimate: ReportCheckEstimate | null;
  busy: ReportCheckBusy;
  error: string | null;
  load: (projectId: string) => Promise<void>;
  refreshEstimate: (projectId: string) => Promise<void>;
  start: (projectId: string) => Promise<void>;
  setFindingStatus: (
    projectId: string,
    checkId: string,
    findingId: string,
    status: FindingStatus,
    note?: string,
  ) => Promise<void>;
  applyEvent: (evt: ReportCheckEvent) => void;
  startPolling: (projectId: string) => void;
  stopPolling: () => void;
  reset: () => void;
}

const POLL_MS = 5000;
let timer: ReturnType<typeof setInterval> | null = null;
// Bayat yanıt kapısı: yalnız EN SON başlatılan `load` yazar (SSE/yoklama/sekme yüklemeleri sırasız
// dönebilir); bulgu PATCH'i sürerken ya da ondan önce başlamış bir `load` optimistik durumu eski
// sunucu durumuyla ezmesin — atılır ve yazımlar bitince bir kez yeniden yüklenir.
let loadSeq = 0;
let writeEpoch = 0;
let pendingWrites = 0;
let reloadAfterWrites = false;
// `start` POST'u kabul edildi ama yeni koşum henüz `current`'a yüklenmedi: yoklama bu koşum
// görünene dek sürer (load hatası yoklamayı durdurmasın).
let awaitCheckId: string | null = null;

function errCode(e: unknown): string {
  return e instanceof ApiError ? e.code : "unknown";
}

function isActive(s: ReportCheckStatus | undefined): boolean {
  return s === "queued" || s === "running";
}

function countOpen(
  findings: ReportCheckDetail["findings"],
  prev: Record<string, number>,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const k of Object.keys(prev)) out[k] = 0;
  for (const f of findings) {
    if (f.status !== "open") continue;
    for (const code of f.sectionCodes) out[code] = (out[code] ?? 0) + 1;
  }
  return out;
}

const INITIAL = {
  projectId: null,
  current: null,
  history: [],
  estimate: null,
  busy: "idle" as ReportCheckBusy,
  error: null,
};

export const useReportCheckStore = create<ReportCheckState>((set, get) => ({
  ...INITIAL,

  async load(projectId) {
    set({ projectId });
    const seq = ++loadSeq;
    const epoch = writeEpoch;
    try {
      const [current, history] = await Promise.all([
        getCurrentReportCheck(projectId),
        listReportChecks(projectId),
      ]);
      // Review kapanıp (reset) ya da başka projeye geçilip bu istek sonradan bitmişse yazma —
      // aksi halde eski projenin yoklaması yeni projenin store'unu ezer.
      if (get().projectId !== projectId || seq !== loadSeq) return;
      if (epoch !== writeEpoch || pendingWrites > 0) {
        if (pendingWrites > 0) reloadAfterWrites = true;
        else void get().load(projectId);
        return;
      }
      set({ current, history });
      // Sunucu yeni koşumu gösteriyorsa (güncel ya da geçmişte — başarısız koşum `current` olmaz)
      // bekleme biter; sonrası olağan aktif/pasif kararı.
      if (current?.id === awaitCheckId || history.some((h) => h.id === awaitCheckId)) {
        awaitCheckId = null;
      }
      if (isActive(current?.status)) {
        if (get().busy === "idle") set({ busy: "polling" });
        if (timer === null) get().startPolling(projectId);
      } else if (awaitCheckId === null) {
        get().stopPolling();
        if (get().busy === "polling") set({ busy: "idle" });
      }
    } catch (e) {
      if (get().projectId !== projectId || seq !== loadSeq) return;
      // Yükleme düştü: ekran "Denetleniyor…" salt okunurda takılı kalmasın — eski koşum hata
      // metniyle görünür; yeni koşum bekleniyorsa yoklama sürer ve ilk başarılı load onu getirir.
      const busy =
        get().busy === "polling" && !isActive(get().current?.status) ? "idle" : get().busy;
      set({ busy, error: reportCheckErrorText(errCode(e)) });
    }
  },

  async refreshEstimate(projectId) {
    set({ busy: "estimating", error: null });
    try {
      const estimate = await getReportCheckEstimate(projectId);
      set({ estimate, busy: "idle" });
    } catch (e) {
      set({ busy: "idle", error: reportCheckErrorText(errCode(e)) });
    }
  },

  async start(projectId) {
    set({ error: null });
    if (!get().estimate) {
      await get().refreshEstimate(projectId);
    }
    const estimate = get().estimate;
    if (!estimate) return;
    set({ busy: "starting" });
    try {
      const { checkId } = await startReportCheck(projectId, estimate.inputFingerprint);
      awaitCheckId = checkId;
      set({ busy: "polling" });
      await get().load(projectId);
      get().startPolling(projectId);
    } catch (e) {
      const code = errCode(e);
      if (code === "input_changed") {
        await get().refreshEstimate(projectId);
      }
      set({ busy: "idle", error: reportCheckErrorText(code) });
    }
  },

  async setFindingStatus(projectId, checkId, findingId, status, note) {
    const prev = get().current;
    if (!prev) return;
    const findings = prev.findings.map((f) =>
      f.id === findingId ? { ...f, status, statusNote: note ?? f.statusNote } : f,
    );
    set({
      current: {
        ...prev,
        findings,
        openCountBySection: countOpen(findings, prev.openCountBySection),
      },
      error: null,
    });
    writeEpoch += 1;
    pendingWrites += 1;
    try {
      await patchFinding(
        projectId,
        checkId,
        findingId,
        note === undefined ? { status } : { status, note },
      );
    } catch (e) {
      // Yalnız o bulguyu geri al: PATCH sürerken yeni koşum yüklendiyse (`current` değiştiyse)
      // eski koşumu güncel diye geri yazma — yeni koşum ekranda kalır, hata metni görünür.
      const before = prev.findings.find((f) => f.id === findingId);
      const cur = get().current;
      if (before && cur?.id === checkId) {
        const reverted = cur.findings.map((f) => (f.id === findingId ? before : f));
        set({
          current: {
            ...cur,
            findings: reverted,
            openCountBySection: countOpen(reverted, cur.openCountBySection),
          },
        });
      }
      set({ error: reportCheckErrorText(errCode(e)) });
    } finally {
      // PATCH sürerken başlamış ama sunucuyu yazımdan önce okumuş olabilecek load'lar da atılsın.
      writeEpoch += 1;
      pendingWrites -= 1;
      if (pendingWrites === 0 && reloadAfterWrites) {
        reloadAfterWrites = false;
        if (get().projectId === projectId) void get().load(projectId);
      }
    }
  },

  applyEvent(evt) {
    const projectId = get().projectId;
    if (!projectId) return;
    if (evt.status === "done" || evt.status === "failed") {
      get().stopPolling();
      void get().load(projectId);
      return;
    }
    // queued/running: bant durumu hemen güncellenir; load yoklamayı da kurar.
    const cur = get().current;
    if (cur?.id === evt.checkId && cur.status !== evt.status) {
      set({ current: { ...cur, status: evt.status } });
    }
    void get().load(projectId);
  },

  startPolling(projectId) {
    get().stopPolling();
    timer = setInterval(() => {
      if (!isActive(get().current?.status) && awaitCheckId === null) {
        get().stopPolling();
        return;
      }
      void get().load(projectId);
    }, POLL_MS);
  },

  stopPolling() {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  },

  reset() {
    get().stopPolling();
    loadSeq += 1;
    reloadAfterWrites = false;
    awaitCheckId = null;
    set({ ...INITIAL });
  },
}));
